import { useHistory } from 'react-router-dom';

import type { CampaignClass } from '@feathr/blackbox';
import { ECampaignClass, EPackages, getFeatureMap } from '@feathr/blackbox';
import { useFlags, useLocalUrl } from '@feathr/extender/state';
import { useAccount, useUser } from '@feathr/report_components';

import { createCampaignRecord, type TCampaignRecord } from './useLicense.campaigns';
import { assertActivePackage } from './useLicense.utils';

type TCampaignPermissionsQuery =
  | keyof TCampaignRecord
  | Array<keyof TCampaignRecord | CampaignClass>
  | CampaignClass;

/**
 * Returns the full campaign permissions object for the account's active package.
 */
export default function useCampaignPermissions(): TCampaignRecord;
/**
 * Returns the campaign permissions for a single key.
 */
export default function useCampaignPermissions<K extends keyof TCampaignRecord>(
  query: K,
): TCampaignRecord[K];
/**
 * Returns the campaign permissions object for a given array of keys.
 */
export default function useCampaignPermissions<K extends keyof TCampaignRecord>(
  query: K[],
): Pick<TCampaignRecord, K>;
/**
 * Returns the campaign permissions for a CampaignClass value.
 */
export default function useCampaignPermissions(query: CampaignClass): boolean;
/**
 * Returns the campaign permissions for an array that may contain both friendly keys and CampaignClass values.
 */
export default function useCampaignPermissions(
  query: Array<keyof TCampaignRecord | CampaignClass>,
): Partial<TCampaignRecord>;
export default function useCampaignPermissions(
  query?: TCampaignPermissionsQuery,
):
  | TCampaignRecord
  | TCampaignRecord[keyof TCampaignRecord]
  | Pick<TCampaignRecord, keyof TCampaignRecord>
  | Partial<TCampaignRecord>
  | boolean {
  const history = useHistory();
  const localUrl = useLocalUrl();

  const account = useAccount();
  const { isInternal } = useUser();
  const { showPixlPlus } = useFlags();

  // Does not throw error if the user is internal.
  assertActivePackage(account.activePackage, isInternal, history, localUrl);

  const { ads, email, conversations, other, invites, monetization } = getFeatureMap(
    // If account package is invalid and user is internal, use the internal package.
    !account.hasValidLicensePackageName && isInternal
      ? EPackages.Internal
      : account.activePackage.name,
  );

  const campaigns = createCampaignRecord(ads, email, conversations, other, invites, monetization);

  // TODO: Remove this when we no longer need the Pixl Plus flag
  if (!showPixlPlus) {
    campaigns.omni = false;
  }

  if (!query) {
    return campaigns;
  }

  if (Array.isArray(query)) {
    return Object.fromEntries(
      query.map((key) => {
        // Handle CampaignClass values in array
        if (typeof key === 'string' && key.startsWith('Campaign.')) {
          // Find the friendly key that maps to this CampaignClass value
          const friendlyKey = Object.entries(ECampaignClass).find(
            ([, value]) => (value as string) === key,
          )?.[0] as keyof TCampaignRecord | undefined;

          if (friendlyKey && friendlyKey in campaigns) {
            return [friendlyKey, campaigns[friendlyKey]];
          }
          return [key, false];
        }
        return [key, campaigns[key as keyof TCampaignRecord]];
      }),
    ) as Partial<TCampaignRecord>;
  }

  // Handle single CampaignClass values
  if (typeof query === 'string' && query.startsWith('Campaign.')) {
    // Find the friendly key that maps to this CampaignClass value
    const friendlyKey = Object.entries(ECampaignClass).find(
      ([, value]) => (value as string) === query,
    )?.[0] as keyof TCampaignRecord | undefined;

    if (friendlyKey && friendlyKey in campaigns) {
      return campaigns[friendlyKey];
    }
    return false;
  }

  return campaigns[query];
}
